import { todayInParis } from '@cockpit/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { z } from 'zod';
import { lateCount } from '../afaire';
import { api } from '../api';
import { useAuthStore } from './auth';
import { useStreamStore } from './stream';

/**
 * Pastille de l'onglet « À faire » (maquette) : le nombre d'actions en retard, toutes pour l'administrateur,
 * les siennes pour un membre. Rechargée au démarrage, puis à chaque changement (flux SSE : action modifiée,
 * plan exécuté, synchro terminée) et quand l'écran À faire modifie une action (`refresh`).
 */
export const useBadgesStore = defineStore('badges', () => {
  const late = ref(0);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let offs: (() => void)[] = [];

  async function load(): Promise<void> {
    const auth = useAuthStore();
    if (!auth.me) return;
    try {
      const open = await api.actions({ who: auth.me.role === 'ADMIN' ? 'all' : 'me', status: 'open' });
      late.value = lateCount(open, auth.config?.today ?? todayInParis());
    } catch {
      // La pastille est un plus : en cas d'échec, on garde la dernière valeur.
    }
  }

  /** Rechargement groupé (plusieurs événements d'affilée = un seul appel). */
  function refresh(): void {
    clearTimeout(timer);
    timer = setTimeout(() => void load(), 400);
  }

  function start(): void {
    stop();
    const stream = useStreamStore();
    offs = ['action.changed', 'plan.executed', 'sync.done'].map((type) =>
      stream.on(type, z.unknown(), refresh),
    );
    void load();
  }

  function stop(): void {
    for (const off of offs) off();
    offs = [];
    clearTimeout(timer);
  }

  return { late, load, refresh, start, stop };
});
